import { observer, Provider } from 'mobx-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { IntlProvider, ReactIntlErrorCode } from 'react-intl';
import { IntlProviderWithContext } from 'IntlProviderWithContext';
import { store, StoreContext } from 'Models/Store';
import { TwoFactorContext, TwoFactorMethods } from 'Services/TwoFactor/Common';
import { BrowserRouter, Route } from 'react-router-dom';
import { ObservabilityRoutes } from 'Util/_HumanWritten/Observability/ObservabilityRoutes';
import { ToastContainer } from 'react-toastify';
import Admin from 'Views/Admin';
import Frontend from 'Views/Frontend';
import GlobalModal from 'Views/Components/Modal/GlobalModal';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
	getCurrentLanguageLocale,
	getCurrentLanguageMessages,
	languageStore,
	retrieveLanguagesAsync,
} from 'Stores/LanguageStore';
import { configure } from 'mobx';
import { configureEmail2fa } from 'Services/TwoFactor/Email';
import { OutdatedAppModal } from 'Views/Components/_HumanWritten/OutdatedAppModal/OutdatedAppModal';
import { SessionInterceptor } from 'Modules/Auth/SessionInterceptor';
import { PostLoginProfileCheck } from 'Modules/Auth/PostLoginProfileCheck';
import { setupJourneyElementsAsync } from './Global/JourneyElements/setupJourneyElementsAsync';
import { useFerryScheduleLocationStore } from './Modules/FerrySchedule/Stores/FerryScheduleLocationStore';
import { whiteLabelStore } from './Models/WhiteLabelStore';
import { journeyElementsStore } from './Global/JourneyElements/JourneyElementsStore';
import { retrieveOrganisationsAsync } from './Global/Organisations/OrganisationsStore';
import locationAndRoutesStore from './Models/LocationAndRoutesStore';
import { retrieveUserDisplayNamesAsync } from './Global/User/UserDisplayNameStore';
import passengerTypeStore from './Models/PassengerTypeStore';
import addOnStore from './Models/AddOnStore';
import useAsync from './Hooks/useAsync';

export const App = observer(() => {
	const [locale, setLocale] = useState(getCurrentLanguageLocale());
	const [messages, setMessages] = useState(getCurrentLanguageMessages());

	/**
	 * Bumped when the wording on screen is replaced, and used as a key to remount the app below.
	 *
	 * <p>New messages in state are not enough on their own. Most of the application reads its wording
	 * imperatively — `getMessageByKey(...)` evaluated during render rather than a subscribed value —
	 * and most of the tree is wrapped in MobX's `observer()`, which is `React.memo`, so a re-render
	 * here stops at the first memoised child whose props have not changed. The components that already
	 * rendered the old wording therefore keep it, which is why a saved translation only appeared after
	 * a page refresh.</p>
	 *
	 * <p>Remounting under a key is that refresh, without the reload: everything re-reads on the way
	 * back up, and the query cache, the MobX store and the session all stay where they are. Making
	 * every one of those reads reactive would be the other fix, and it is hundreds of call sites.</p>
	 */
	const [languageVersion, setLanguageVersion] = useState(0);

	/** The wording currently on screen, to tell a real change from the store being written again. */
	const renderedMessages = useRef('');
	const flattenedMessages: Record<string, string> = useMemo(() => {
		return Object.fromEntries(Object.entries(messages).map(([key, value]) => [key, value.defaultMessage]));
	}, [messages]);
	const modalRef = useRef<any>(null);

	const queryClient = useMemo(
		() =>
			new QueryClient({
				defaultOptions: {
					queries: {
						refetchOnWindowFocus: false,
						retry: 1,
						staleTime: 30000, // 30 seconds
					},
				},
			}),
		[],
	);

	const twoFactorMethods = useMemo<TwoFactorMethods>(() => {
		const methods: TwoFactorMethods = {};
		configureEmail2fa(methods);
		return methods;
	}, []);

	// Initialize configuration and stores
	useEffect(() => {
		// All state changes should be run in an action so enforce that
		configure({ enforceActions: 'observed' });

		// For cross platform mobile responsiveness
		const defineViewportHeight = () => {
			const vh = window.innerHeight * 0.01;
			document.documentElement.style.setProperty('--vh', `${vh}px`);
		};
		defineViewportHeight();
		window.addEventListener('resize', defineViewportHeight);
		window.addEventListener('orientationchange', defineViewportHeight);

		return () => {
			window.removeEventListener('resize', defineViewportHeight);
			window.removeEventListener('orientationchange', defineViewportHeight);
		};
	}, []);

	// Subscribe to language store changes
	useEffect(() => {
		return languageStore.subscribe(() => {
			const nextMessages = getCurrentLanguageMessages();
			const serialised = JSON.stringify(nextMessages);
			if (serialised === renderedMessages.current) {
				return;
			}

			// Whether anything has been rendered with wording yet. The first population happens during
			// start-up, before the tree has read any of it, so it needs no remount — only a later change
			// (a translation saved in the admin, or the customer switching language) does.
			const isReplacingWording = renderedMessages.current !== '' && renderedMessages.current !== '{}';

			renderedMessages.current = serialised;
			setLocale(getCurrentLanguageLocale());
			setMessages(nextMessages);

			if (isReplacingWording) {
				setLanguageVersion(version => version + 1);
			}
		});
	}, []);

	// Update modal ref
	useEffect(() => {
		if (modalRef.current) {
			store.modal = modalRef.current;
		}
	}, []);

	return (
		<QueryClientProvider client={queryClient}>
			<IntlProvider
				locale={locale}
				messages={flattenedMessages}
				onError={err => {
					// A missing key is expected — the in-code `defaultMessage` is used as the fallback,
					// so don't spam the console for every un-seeded translation. Real formatting/data
					// errors are still surfaced. (react-intl re-exports the error-code enum as
					// `ReactIntlErrorCode`, not `IntlErrorCode`.)
					if (err.code === ReactIntlErrorCode.MISSING_TRANSLATION) {
						return;
					}
					// eslint-disable-next-line no-console
					console.error(err);
				}}
			>
				<IntlProviderWithContext>
					<TwoFactorContext.Provider value={twoFactorMethods}>
						<StoreContext.Provider value={store}>
							<OutdatedAppModal />
							<Provider store={store}>
								{/* Keyed so replaced wording remounts the tree — see languageVersion. */}
								<AppContent key={languageVersion} />
							</Provider>
						</StoreContext.Provider>
					</TwoFactorContext.Provider>
				</IntlProviderWithContext>
			</IntlProvider>
		</QueryClientProvider>
	);
});

const AppContent = () => {
	useAsync(async () => {
		// Journey elements must resolve before we can derive the default ferry-schedule locations,
		// so that ordering is preserved in its own chain. Everything else loads concurrently rather
		// than as a serial waterfall of round trips.
		const journeyElementsReady = setupJourneyElementsAsync().then(() => {
			const state = useFerryScheduleLocationStore.getState();
			if (state.startLocationId === '') {
				const defaultRouteId = whiteLabelStore.defaultRouteId ?? '';
				const defaultRoute = journeyElementsStore.getState().getRouteById(defaultRouteId);
				if (defaultRoute) {
					useFerryScheduleLocationStore.getState().setLocations({
						startLocationId: defaultRoute.departureId,
						endLocationId: defaultRoute.destinationId,
					});
				}
			}
		});

		// Loaded concurrently. passengerTypes and add-ons are loaded here (not deferred) because they're
		// read directly — not always via their usePassengerTypes/useAddOns hooks — across bookings,
		// promotions, pricing, check-in, account and the booking wizard, AND their stores are not
		// MobX-reactive, so a direct reader that renders before they load would show stale data with no
		// recovery. Payment config stays deferred: it's only needed at checkout and PaymentProviderWrapper
		// loads it on demand (paymentStore.isReady is observable, so it updates reactively).
		await Promise.all([
			journeyElementsReady,
			retrieveOrganisationsAsync(),
			retrieveUserDisplayNamesAsync(),
			retrieveLanguagesAsync(),
			locationAndRoutesStore.retrieveAsync(true),
			passengerTypeStore.retrieveAsync(true),
			addOnStore.retrieveAddOnsAsync(true),
		]);
	}, []);

	return (
		<>
			<BrowserRouter>
				<SessionInterceptor />
				<PostLoginProfileCheck />
				<ToastContainer limit={3} className="frontend" />
				<ObservabilityRoutes>
					<Route path="/admin-backend/*" element={<Admin />} />
					<Route path="/*" element={<Frontend />} />
				</ObservabilityRoutes>
			</BrowserRouter>
			<GlobalModal
				ref={ref => {
					if (ref) {
						store.modal = ref;
					}
				}}
			/>
		</>
	);
};
